// 🔴 P1 (démo Laurent) — une photo ne se perd JAMAIS en silence.
//
// File d'envoi des photos d'une visite, tenue HORS du composant (module) : fermer le menu ☰, changer d'écran ou démonter PhotosReleve ⛔ n'arrête pas un envoi en cours
// et ⛔ n'efface pas un échec. Chaque photo a son état (attente · envoi · échec) ; une photo en échec est CONSERVÉE EN MÉMOIRE DE LA PAGE (le fichier lui-même)
// pour « Réessayer » ou « Retirer ». Un échec dans un lot n'arrête PAS les suivantes. HEIC / format non géré / > 8 Mo dont la réduction a échoué : message explicite
// AVANT l'envoi. ⚠️ Mémoire de la page seulement : fermer l'onglet perd les photos non envoyées (une file persistante = IndexedDB + reprise, hors périmètre).
export const TAILLE_MAX = 8 * 1024 * 1024;
export const TYPES_ACCEPTES = ["image/jpeg", "image/png", "image/webp"];

const VIDE = Object.freeze({ items: Object.freeze([]), ok: 0 });
const etats = new Map();           // releveId -> { items, ok }  (objets immuables : remplacés à chaque changement)
const ecoutes = new Set();
let cle = 0;

const emettre = () => ecoutes.forEach((f) => f());
const lire = (rid) => etats.get(rid) || VIDE;
const ecrire = (rid, suite) => { etats.set(rid, suite(lire(rid))); emettre(); };
const modifier = (rid, c, patch) => ecrire(rid, (e) => ({ ...e, items: e.items.map((i) => (i.cle === c ? { ...i, ...patch } : i)) }));

export const abonner = (f) => { ecoutes.add(f); return () => ecoutes.delete(f); };
export const etat = lire;
export const nonEnvoyees = (rid) => lire(rid).items.filter((i) => i.etat !== "ok").length;
const RECENTES_MAX = 10;                // photos envoyées gardées en mémoire de la page pour « Enregistrer sur le téléphone »
export const effacerTout = () => { etats.clear(); emettre(); };      // tests

/** Refus connu AVANT l'envoi (le serveur n'accepte que JPEG/PNG/WebP ≤ 8 Mo). Rend un message lisible ou null. */
export function refusAvantEnvoi(fichier, reduite) {
  if (reduite) return null;                                           // réduite = JPEG léger
  const type = String(fichier.type || "").toLowerCase();
  if (type === "image/heic" || type === "image/heif" || /\.hei[cf]$/i.test(fichier.name || "")) {
    return "Format HEIC non pris en charge : réglez l'appareil photo en JPEG (Réglages › Appareil photo › Formats › Le plus compatible), puis reprenez la photo.";
  }
  if (!TYPES_ACCEPTES.includes(type)) return `Format « ${type || "inconnu"} » non pris en charge : JPEG, PNG ou WebP attendu.`;
  if (fichier.size > TAILLE_MAX) return "Photo trop lourde (8 Mo maximum) et impossible à réduire sur cet appareil : reprenez-la en qualité moyenne.";
  return null;
}

const message = (err) => (typeof navigator !== "undefined" && navigator.onLine === false)
  ? "Hors connexion : la photo n'est pas envoyée. Réessayez dès que le réseau revient."
  : (err?.message || "Envoi impossible");

async function traiter(api, rid, c, reduire) {
  const item = lire(rid).items.find((i) => i.cle === c);
  if (!item) return;
  modifier(rid, c, { etat: "envoi", erreur: null });
  try {
    let { fichier, reduite } = item.prepare || (await reduire(item.fichier));
    modifier(rid, c, { prepare: { fichier, reduite } });
    const refus = refusAvantEnvoi(fichier, reduite);
    if (refus) { modifier(rid, c, { etat: "echec", erreur: refus, definitif: true }); return; }
    await api.ajouterPhoto(rid, fichier, item.posteId);
    // Envoyée : on GARDE la photo d'origine (pleine qualité) en mémoire de la page, marquée « ok », pour proposer « Enregistrer sur le téléphone ».
    ecrire(rid, (e) => {
      const items = e.items.map((i) => (i.cle === c ? { ...i, etat: "ok", erreur: null, prepare: null } : i));
      const ok = items.filter((i) => i.etat === "ok");
      const aGarder = new Set(ok.slice(-RECENTES_MAX).map((i) => i.cle));
      return { items: items.filter((i) => i.etat !== "ok" || aGarder.has(i.cle)), ok: e.ok + 1 };
    });
  } catch (err) {
    modifier(rid, c, { etat: "echec", erreur: message(err), definitif: false });
  }
}

/** Dépose un lot : chaque photo a son état, l'échec de l'une n'arrête pas les autres. Rend une promesse résolue quand le lot est traité. */
export async function deposer(api, rid, fichiers, posteId, reduire) {
  const nouveaux = fichiers.map((f) => ({ cle: `p${++cle}`, fichier: f, nom: f.name || "photo", posteId: posteId || null, etat: "attente", erreur: null }));
  ecrire(rid, (e) => ({ ...e, items: [...e.items, ...nouveaux] }));
  for (const n of nouveaux) await traiter(api, rid, n.cle, reduire);
}

export const reessayer = (api, rid, c, reduire) => traiter(api, rid, c, reduire);
export const retirer = (rid, c) => ecrire(rid, (e) => ({ ...e, items: e.items.filter((i) => i.cle !== c) }));
